import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { IOrderManagementState } from "@/app_redux/reducers/slices/auth/order_management_slice";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import React, { MouseEvent, useEffect, useState } from "react";
import InnerImageZoom from "react-inner-image-zoom";
import "react-inner-image-zoom/lib/InnerImageZoom/styles.css";

export default function ImgPreview({ data, imageitem }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageUrls, setImageUrls] = useState<{ [key: number]: string }>({});

  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("ORDER_MANAGEMENT", "id");
  const { routeState } = useRouteInfo(routeId);

  const { isUserPopupModalActive } =
    routeState.state as IOrderManagementState;

  // Sync current index when imageitem changes
  useEffect(() => {
    const itemIndex = data?.findIndex(
      (item) => item?.vetic_variant_id === imageitem?.vetic_variant_id
    );
    setCurrentImageIndex(itemIndex >= 0 ? itemIndex : 0);
  }, [imageitem, data]);


  // Determine if the "Prev" button should be disabled
  const isPrevButtonDisabled = currentImageIndex === 0;

  // Determine if the "Next" button should be disabled
  const isNextButtonDisabled = currentImageIndex === data?.length - 1;

  // Carousel functionality
  const nextImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isNextButtonDisabled) {
      setCurrentImageIndex((prevIndex) => prevIndex + 1);
    }
  };

  const prevImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isPrevButtonDisabled) {
      setCurrentImageIndex((prevIndex) => prevIndex - 1);
    }
  };
  useEffect(() => {
    const fetchImage = async () => {
      const currentItem = data?.[currentImageIndex];
      if (!currentItem || imageUrls[currentImageIndex]) return;

      try {
        const response = await fetch(currentItem.thumbnail, { mode: "cors" });

        if (!response.ok) throw new Error("Fetch failed");

        const arrayBuffer = await response.arrayBuffer();
        const blob = new Blob([arrayBuffer], { type: "image/jpeg" });

        // If blob has size 0, fallback
        if (blob.size === 0) throw new Error("Empty blob");

        const url = URL.createObjectURL(blob);
        setImageUrls((prev) => ({ ...prev, [currentImageIndex]: url }));
      } catch (error) {
        console.error("Error loading image, falling back to original:", error);
        // fallback to original URL
        setImageUrls((prev) => ({ ...prev, [currentImageIndex]: currentItem.thumbnail }));
      }
    };

    if (open) {
      fetchImage();
    }
  }, [currentImageIndex, open, data, imageUrls]);

  // cleanup only when modal closes or unmounts
  useEffect(() => {
    if (!open) {
      return () => {
        Object.values(imageUrls).forEach((url) => {
          if (url.startsWith("blob:")) {
            URL.revokeObjectURL(url);
          }
        });
      };
    }
  }, [open, imageUrls]);
  useEffect(() => {
    if (!isUserPopupModalActive) {
      setCurrentImageIndex(0)
      setImageUrls({})
    }
  }, [isUserPopupModalActive])

  const imageURL = imageUrls[currentImageIndex] || data?.[currentImageIndex]?.thumbnail;

  return (
    <div>
      <div className="h-auto flex justify-center flex-col items-center gap-2">
        <div className="relative h-[11rem] w-[11rem]">
          <InnerImageZoom
            src={imageURL}
            zoomSrc={imageURL}
            zoomType="hover"
            zoomPreload={true}
            alt={data?.[currentImageIndex]?.item_name || "image"}
            className="w-full h-full"
          />
        </div>
        <div className="flex items-center gap-2 py-2">
          <button
            className="cursor-pointer border py-1 px-2 rounded-md hover:text-white hover:bg-green-600 font-bold"
            onClick={(e) => prevImage(e)}
            disabled={isPrevButtonDisabled}
          >
            <ArrowLeftIcon className="w-4 h-4" />
          </button>
          <button
            className="cursor-pointer border py-1 px-2 rounded-md hover:text-white hover:bg-green-600 font-bold"
            onClick={(e) => nextImage(e)}
            disabled={isNextButtonDisabled}
          >
            <ArrowRightIcon className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs text-center">
          {data?.[currentImageIndex]?.item_name}{" "}
          <span className="text-xs font-bold">
            - {data?.[currentImageIndex]?.quantity_to_pick} Qty
          </span>
        </p>
      </div>
    </div>
  );
}
